import { useEffect, useState } from 'react';
import AccordionBasic from '../../../../../components/accordions/AccordionBasic';
import DropdownFilter from '../../../../../components/dropdowns/DropdownFilter';
import { useData } from '../../../../../hooks/useData';
import StandardButton from '../../../../../components/buttons/StandardButton';
import { getFranchiseReportConfigs, updateFranchiseReportConfigs } from '@/utils/api';
import { FranchiseUser } from '@/types/FranchiseUser';
import { ReportScheduleConfig } from '@/types/ReportScheduleConfig';
import { Toaster, toast } from 'sonner';


const FranchiseReportEmailsConfiguration: React.FC = () => {
  const {
    selectedFranchise, franchiseUsers
  } = useData();
  const [franchiseReportConfigs, setFranchiseReportConfigs] = useState<ReportScheduleConfig[]>([]);
  const [selectedDailyFranchiseUsers, setSelectedDailyFranchiseUsers] = useState<
    Array<{ id: string; value: string }>
  >([]);
  const [selectedMonthlyFranchiseUsers, setSelectedMonthlyFranchiseUsers] = useState<
    Array<{ id: string; value: string }>
  >([]);
  const [selectedWeeklyFranchiseUsers, setSelectedWeeklyFranchiseUsers] = useState<
    Array<{ id: string; value: string }>
  >([]);
  const [displayedUsers, setDisplayedUsers] = useState<
    FranchiseUser[] | Array<{ userId: string; email: string }>
  >([]);



  const handleSelectedFranchiseDailyUsersChange = (id: string) => {
    const matched = franchiseUsers.find((user) => user.userId === id);
    if (matched != null) {
      setSelectedDailyFranchiseUsers((prevSelected) =>
        prevSelected.some((user) => user.id === matched.userId)
          ? prevSelected.filter((user) => user.id !== matched.userId)
          : [...prevSelected, { id: matched.userId, value: matched.email }]
      );
    }
  };
  const handleSelectedFranchiseWeeklyUsersChange = (id: string) => {
    const matched = franchiseUsers.find((user) => user.userId === id);
    if (matched != null) {
      setSelectedWeeklyFranchiseUsers((prevSelected) =>
        prevSelected.some((user) => user.id === matched.userId)
          ? prevSelected.filter((user) => user.id !== matched.userId)
          : [...prevSelected, { id: matched.userId, value: matched.email }]
      );
    }
  };
  const handleSelectedFranchiseMonthlyUsersChange = (id: string) => {
    const matched = franchiseUsers.find((user) => user.userId === id);
    if (matched != null) {
      setSelectedMonthlyFranchiseUsers((prevSelected) =>
        prevSelected.some((user) => user.id === matched.userId)
          ? prevSelected.filter((user) => user.id !== matched.userId)
          : [...prevSelected, { id: matched.userId, value: matched.email }]
      );
    }
  };

  const naturalSortOptions = (
    options: Array<{ id: string; value: string }>
  ) => {
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    return [...options].sort((a, b) => collator.compare(a.value, b.value));
  };
  const handleSave = async () => {
    if (!selectedFranchise) return;
    for (const reportConfig of franchiseReportConfigs) {

      if (reportConfig.displayName === "Default Franchise Monthly Report Schedule") {

        reportConfig.emails = selectedMonthlyFranchiseUsers.map(user => user.value);


      }
      else if (reportConfig.displayName === "Default Franchise Daily Report Schedule") {
        reportConfig.emails = selectedDailyFranchiseUsers.map(user => user.value);
      }
      else if (reportConfig.displayName === "Default Franchise Weekly Report Schedule") {
        reportConfig.emails = selectedWeeklyFranchiseUsers.map(user => user.value);
      }
    }

    await updateFranchiseReportConfigs(selectedFranchise.id, franchiseReportConfigs).then(() => toast('Successfully updated report config.'))
      .then(async () => {
        await fetchFranchiseScheduleConfigs();
      });
  }
  const fetchFranchiseScheduleConfigs = async (): Promise<void> => {
    if (selectedFranchise != null && franchiseUsers.length != 0) {
      const newReportScheduleConfigs = await getFranchiseReportConfigs(
        selectedFranchise?.id
      );

      setDisplayedUsers(franchiseUsers);
      setFranchiseReportConfigs([
        ...newReportScheduleConfigs
      ]);

      // iterate over franchiseReportConfigs and assign correct user lists to each list
      for (const reportConfig of newReportScheduleConfigs as ReportScheduleConfig) {
        if (reportConfig.displayName === "Default Franchise Monthly Report Schedule") {
          setSelectedMonthlyFranchiseUsers(
            reportConfig.emails.map((email) => {
              const user = franchiseUsers.find((user) => user.email === email);
              return { id: user?.userId ?? email, value: email };
            })
          );
        }
        else if (reportConfig.displayName === "Default Franchise Daily Report Schedule") {
          setSelectedDailyFranchiseUsers(
            reportConfig.emails.map((email) => {
              const user = franchiseUsers.find((user) => user.email === email);
              return { id: user?.userId ?? email, value: email };
            })
          );
        }
        else if (reportConfig.displayName === "Default Franchise Weekly Report Schedule") {
          setSelectedWeeklyFranchiseUsers(
            reportConfig.emails.map((email) => {
              const user = franchiseUsers.find((user) => user.email === email);
              return { id: user?.userId ?? email, value: email };
            })
          );
        }
      }
    }
  };
  function handleFilter(event: React.ChangeEvent<HTMLInputElement>): void {
    event.preventDefault();
    const val = event.target.value.toLowerCase();


    const filteredUsers = franchiseUsers.filter((user) => {
      if (user.email.toLowerCase().includes(val)) {
        return true;
      }

      if (user.email.toLowerCase().includes(val)) {
        return true;
      }

      return false;
    });

    setDisplayedUsers(filteredUsers);
  }
  useEffect(() => {
    void fetchFranchiseScheduleConfigs();

  }, [selectedFranchise, franchiseUsers]);
  return (
    <AccordionBasic title={"Email Reports"} dataCy="franchise-email-accordion">
      <Toaster />
      <div className="flex flex-wrap w-full gap-4">
        {/* Change Email */}
        <div className="flex flex-col gap-4">
          <label>Users to get daily email reports</label>
          <DropdownFilter
            title={`${selectedDailyFranchiseUsers.length} Recipients`}
            options={naturalSortOptions(
              displayedUsers.map((user) => ({
                id: (user as FranchiseUser).userId,
                value: (user as FranchiseUser).email,
              }))
            )}
            filterFunction={handleFilter}
            selected={selectedDailyFranchiseUsers.map((user) => ({
              id: user.id,
              value: user.value,
            }))}
            handleChange={handleSelectedFranchiseDailyUsersChange}
          />
          <label>Users to get weekly email reports</label>
          <DropdownFilter
            title={`${selectedWeeklyFranchiseUsers.length} Recipients`}
            options={naturalSortOptions(
              displayedUsers.map((user) => ({
                id: (user as FranchiseUser).userId,
                value: (user as FranchiseUser).email,
              }))
            )}
            filterFunction={handleFilter}
            selected={selectedWeeklyFranchiseUsers.map((user) => ({
              id: user.id,
              value: user.value,
            }))}
            handleChange={handleSelectedFranchiseWeeklyUsersChange}
          />
          <label>Users to get monthly email reports</label>
          <DropdownFilter
            title={`${selectedMonthlyFranchiseUsers.length} Recipients`}
            options={naturalSortOptions(
              displayedUsers.map((user) => ({
                id: (user as FranchiseUser).userId,
                value: (user as FranchiseUser).email,
              }))
            )}
            filterFunction={handleFilter}
            selected={selectedMonthlyFranchiseUsers.map((user) => ({
              id: user.id,
              value: user.value,
            }))}
            handleChange={handleSelectedFranchiseMonthlyUsersChange}
          />
          <div className="flex flex-wrap gap-8 w-full">
            <StandardButton onClick={handleSave} dataCy="update-mailing-list">
              Save Updates
            </StandardButton>
          </div>
        </div>
      </div>
    </AccordionBasic>
  );
};

export default FranchiseReportEmailsConfiguration;
